/**
 * QuickActionsBar Component
 *
 * Displays quick action buttons for a connected database.
 * Actions are disabled when connection is disconnected.
 */

import { useState, useCallback } from 'react';
import {
  FileCode,
  Table,
  Settings,
  Network,
  LayoutDashboard,
} from 'lucide-react';
import { Button } from '../../../../components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../../../components/ui/tooltip';
import { useAnalytics } from '@/core/analytics/useAnalytics';
import { useConnectionStore } from '../../../connections/store/connectionStore';
import { TableDesignerDialog } from '../../table-designer/components/TableDesignerDialog';
import { DatabaseSelectorDialog } from '../../../toolbar-context/components/DatabaseSelectorDialog';
import type { ComputedConnectionStatus } from '../utils/connectionStatus';
import { engineSupports, getEngineDisplayInfo, type DatabaseEngineId } from '@/core/engines';
import {
  dashboardsUnsupportedReason,
  queryEditorUnsupportedReason,
  supportsDashboardsEngineFilter,
  supportsTableDesignerEngineFilter,
  tableDesignerUnsupportedReason,
  tableExplorerUnsupportedReason,
} from '../../../main-toolbar/model/toolbarEngineFilters';
import { useBetaFeature } from '@/core/beta';
import type { Connection } from '../../../../types/electron';
import { StorageQuickActions } from './StorageQuickActions';
import { safeConnectionAlias, safeConnectionName } from '@/types/safeConnectionDisplay';

type QuickActionsBarProps = {
  connectionStatus: ComputedConnectionStatus;
  onNewQuery: (connectionId: string, database?: string) => void;
  onTableExplorer: (connectionId: string, database: string) => void;
  onSchemaOverview: (connectionId: string, database: string) => void;
  onOpenDashboards: () => void;
  /** Azure Storage cards offer Connection info among their actions (round B U6). */
  onConnectionInfo?: (connection: Connection) => void;
};

type ActionButtonProps = {
  icon: React.ReactNode;
  label: string;
  tooltip: string;
  onClick: () => void;
  disabled?: boolean;
  /** Reason shown in the tooltip while disabled (defaults to a connect prompt) */
  disabledTooltip?: string;
};

function ActionButton({
  icon,
  label,
  tooltip,
  onClick,
  disabled = false,
  disabledTooltip,
}: ActionButtonProps) {
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          {/* Span keeps the tooltip reachable while the button is disabled
              (disabled buttons get pointer-events-none and never fire hover) */}
          <span className="inline-block w-full" tabIndex={disabled ? 0 : -1}>
            <Button
              variant="ghost"
              size="sm"
              className="h-8 px-2 gap-1.5 w-full justify-start"
              onClick={onClick}
              disabled={disabled}
              data-testid={`quick-action-${label.toLowerCase().replace(/\s+/g, '-')}`}
            >
              {icon}
              <span className="text-xs">{label}</span>
            </Button>
          </span>
        </TooltipTrigger>
        <TooltipContent side="bottom">
          <p>{disabled ? disabledTooltip ?? `Connect to enable ${label}` : tooltip}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

// Pending action types for database selector
type PendingAction = 'query' | 'explorer' | 'designer' | 'overview';

/**
 * Round B U6: an Azure Storage connection gets the storage actions (every SQL action refuses it — the card used to be
 * five disabled buttons); every other engine keeps the SQL set below, unchanged.
 */
export function QuickActionsBar(props: QuickActionsBarProps) {
  const { connection, status } = props.connectionStatus;
  if (connection.engine === 'azurestorage') {
    return <StorageQuickActions connection={connection} isConnected={status === 'connected'} onConnectionInfo={props.onConnectionInfo} />;
  }
  return <SqlQuickActions {...props} />;
}

function SqlQuickActions({
  connectionStatus,
  onNewQuery,
  onTableExplorer,
  onSchemaOverview,
  onOpenDashboards,
}: QuickActionsBarProps) {
  const { track } = useAnalytics();
  const { connection, status, database } = connectionStatus;
  const isConnected = status === 'connected';
  const engine = (connection.engine ?? 'mssql') as DatabaseEngineId;
  const isSqlite = engine === 'sqlite';
  const connectionId = connection.id;
  const connectionName = safeConnectionName(connection, 'Connection');

  // Connection store for updating default database
  const updateConnection = useConnectionStore((state) => state.updateConnection);

  // Dialog states
  const [showTableDesignerDialog, setShowTableDesignerDialog] = useState(false);
  const [showDatabaseSelector, setShowDatabaseSelector] = useState(false);
  const [pendingAction, setPendingAction] = useState<PendingAction | null>(null);
  const [selectedDb, setSelectedDb] = useState<string>('');

  // For start page, we only use the connection's configured database - NOT the object explorer context
  // This ensures consistency since the connection tile might be for a different connection than
  // the one selected in the object explorer
  const hasExplicitDatabase = Boolean(database || connection.database);

  // Determine effective database - only use connection config, not object explorer
  const getEffectiveDatabase = (): string => {
    // Try the database from active connection state
    if (database) return database;

    // Then try the database from connection config
    if (connection.database) return connection.database;

    // Use sensible defaults when no database is specified
    if (isSqlite) return safeConnectionAlias(connection, '');
    // For server-based engines, default to their admin databases
    if (engine === 'mssql') return 'master';
    if (engine === 'pgsql') return 'postgres';
    return '';
  };

  const effectiveDatabase = getEffectiveDatabase();
  // Engine-gated actions. Schema Overview (kusto lacks it) mirrors the main
  // toolbar's supportsSchemaOverviewEngineFilter; New Query / Explorer are the
  // S1 Task 1.16 capability gates (false only for Azure Storage today).
  const supportsSchemaOverview = engineSupports(
    engine,
    'workspaceFeatures',
    'supportsSchemaOverview'
  );
  const supportsQueryEditor = engineSupports(engine, 'workspaceFeatures', 'supportsQueryEditor');
  const supportsTableExplorer = engineSupports(engine, 'workspaceFeatures', 'supportsTableExplorer');
  // UI review F4: Designer / Dashboard are capability-gated too (Kusto and Azure Storage create no tables; Azure Storage
  // has no query language for dashboard tiles) — never by engine name.
  const supportsTableDesigner = supportsTableDesignerEngineFilter(engine);
  const supportsDashboards = supportsDashboardsEngineFilter(engine);

  // Handle database selection from the selector dialog
  const handleDatabaseSelected = useCallback(
    async (db: string, setAsDefault?: boolean) => {
      setShowDatabaseSelector(false);

      // Update connection's default database if requested
      if (setAsDefault) {
        const updatedConnection = { ...connection, database: db };
        await updateConnection(updatedConnection);
      }

      // Execute the pending action with the selected database
      const action = pendingAction;
      setPendingAction(null);
      setSelectedDb(db);

      switch (action) {
        case 'query':
          onNewQuery(connectionId, db);
          break;
        case 'explorer':
          onTableExplorer(connectionId, db);
          break;
        case 'designer':
          setSelectedDb(db);
          setShowTableDesignerDialog(true);
          break;
        case 'overview':
          onSchemaOverview(connectionId, db);
          break;
      }
    },
    [connection, connectionId, pendingAction, onNewQuery, onTableExplorer, onSchemaOverview, updateConnection]
  );

  // Helper to execute action or show database selector
  const executeWithDatabase = useCallback(
    (action: PendingAction) => {
      // Capability gate BEFORE any database resolution — the buttons are
      // already disabled, this keeps a programmatic call honest too.
      if (action === 'query' && !supportsQueryEditor) return;
      if (action === 'explorer' && !supportsTableExplorer) return;
      if (action === 'designer' && !supportsTableDesigner) return;
      // For SQLite, always proceed (no database selection needed)
      if (isSqlite) {
        switch (action) {
          case 'query':
            onNewQuery(connectionId, effectiveDatabase);
            break;
          case 'explorer':
            onTableExplorer(connectionId, effectiveDatabase);
            break;
          case 'designer':
            setSelectedDb(effectiveDatabase);
            setShowTableDesignerDialog(true);
            break;
          case 'overview':
            onSchemaOverview(connectionId, effectiveDatabase);
            break;
        }
        return;
      }

      // If we have an explicit database selection, proceed directly
      if (hasExplicitDatabase) {
        switch (action) {
          case 'query':
            onNewQuery(connectionId, effectiveDatabase);
            break;
          case 'explorer':
            onTableExplorer(connectionId, effectiveDatabase);
            break;
          case 'designer':
            setSelectedDb(effectiveDatabase);
            setShowTableDesignerDialog(true);
            break;
          case 'overview':
            onSchemaOverview(connectionId, effectiveDatabase);
            break;
        }
        return;
      }

      // No explicit database - show selector
      setPendingAction(action);
      setShowDatabaseSelector(true);
    },
    [isSqlite, hasExplicitDatabase, effectiveDatabase, connectionId, onNewQuery, onTableExplorer, onSchemaOverview, supportsQueryEditor, supportsTableExplorer, supportsTableDesigner]
  );

  // All actions require database selection
  const dashboardsBetaEnabled = useBetaFeature('dashboards');

  const handleNewQuery = () => {
    executeWithDatabase('query');
  };

  const handleTableExplorer = () => {
    executeWithDatabase('explorer');
  };

  const handleTableDesigner = () => {
    executeWithDatabase('designer');
  };

  const handleSchemaOverview = () => {
    executeWithDatabase('overview');
  };

  // Dashboards only needs a live connection — the starter zero-state (S21)
  // does its own connection/database picking, so this bypasses
  // executeWithDatabase's database-selector prompt entirely.
  const handleOpenDashboards = () => {
    track('feature:dashboard_open_from_home');
    onOpenDashboards();
  };

  return (
    <>
      <div
        className="grid grid-cols-2 gap-1 pt-2 border-t border-border/50"
        data-testid="quick-actions-bar"
      >
        <ActionButton
          icon={<FileCode className="h-3.5 w-3.5" />}
          label="New Query"
          tooltip="Create a new query tab"
          onClick={handleNewQuery}
          disabled={!isConnected || !supportsQueryEditor}
          disabledTooltip={
            isConnected && !supportsQueryEditor
              ? queryEditorUnsupportedReason(engine)
              : undefined
          }
        />

        <ActionButton
          icon={<Table className="h-3.5 w-3.5" />}
          label="Explorer"
          tooltip="Browse table data"
          onClick={handleTableExplorer}
          disabled={!isConnected || !supportsTableExplorer}
          disabledTooltip={
            isConnected && !supportsTableExplorer
              ? tableExplorerUnsupportedReason(engine)
              : undefined
          }
        />

        <ActionButton
          icon={<Settings className="h-3.5 w-3.5" />}
          label="Designer"
          tooltip="Open table designer"
          onClick={handleTableDesigner}
          disabled={!isConnected || !supportsTableDesigner}
          disabledTooltip={isConnected && !supportsTableDesigner ? tableDesignerUnsupportedReason(engine) : undefined}
        />

        <ActionButton
          icon={<Network className="h-3.5 w-3.5" />}
          label="Overview"
          tooltip="View database schema"
          onClick={handleSchemaOverview}
          disabled={!isConnected || !supportsSchemaOverview}
          disabledTooltip={
            isConnected && !supportsSchemaOverview
              ? `Schema Overview isn't available for ${getEngineDisplayInfo(engine)?.shortName ?? engine} connections`
              : undefined
          }
        />

        {/* Dashboards is opt-in beta — with it off the grid falls back to a
            clean 2x2 of the four always-present actions. */}
        {dashboardsBetaEnabled && (
          <ActionButton
            icon={<LayoutDashboard className="h-3.5 w-3.5" />}
            label="Dashboard"
            tooltip="Open the Dashboards workspace"
            onClick={handleOpenDashboards}
            disabled={!isConnected || !supportsDashboards}
            disabledTooltip={isConnected && !supportsDashboards ? dashboardsUnsupportedReason(engine) : undefined}
          />
        )}
      </div>

      {/* Database Selector Dialog - shown when no explicit database is selected */}
      <DatabaseSelectorDialog
        open={showDatabaseSelector}
        onOpenChange={(open) => {
          setShowDatabaseSelector(open);
          if (!open) setPendingAction(null);
        }}
        connectionId={connectionId}
        connectionName={connectionName}
        engine={engine}
        onSelect={handleDatabaseSelected}
        showSetAsDefault={true}
      />

      {/* Table Designer Dialog */}
      <TableDesignerDialog
        open={showTableDesignerDialog}
        onOpenChange={setShowTableDesignerDialog}
        connectionId={connectionId}
        database={selectedDb || effectiveDatabase}
      />
    </>
  );
}
